import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Nav, Row, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import ColumnConfiguration from 'src/components/custom_components/ColumnConfiguration';
import CustomTable, {
  Column,
} from 'src/components/custom_components/CustomTable/CustomTable';
import OrderStatusBadge from 'src/components/custom_components/OrderStatusBadge';
import SitesDropdown from 'src/components/custom_components/SitesDropdown';
import GradingReceivedNumberBadge from 'src/components/GradingReceivedNumberBadge';
import ReportViewer from 'src/components/ReportViewer/ReportViewer';
import SearchPatientInfoFormGroup, {
  SearchPatientFormValues,
} from 'src/components/SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

import { Api } from 'src/models/Api.model';
import { getColumnKey } from 'src/models/ColumnConfiguration.model';
import { CriteriaGroupGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { RetinalExamKeyFiledEnum } from 'src/models/GalenData/CustomDeviceDataModel.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData, OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { I18NextNS } from 'src/models/Language.model';
import { transformExamToSession } from 'src/models/Session.model';

import useGetExistingSiteData from 'src/hooks/useGetExistingSiteData';
import { useGetFilterOrder } from 'src/hooks/useGetFilterOrder';
import useGetRetinalExamDataModelConfig from 'src/hooks/useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import { usePollFilteredOrders } from 'src/hooks/usePollFilteredOrders';

import { captureUserActionError } from 'src/utils/errorReporting';

const DEFAULT_PAGE_SIZE = 10;

enum OrderResultsTab {
  NEW_RESULTS = 'NEW_RESULTS',
  PENDING_RESULTS = 'PENDING_RESULTS',
  ORDER_CLOSED = 'CLOSED_ORDERS',
}

const TAB_TO_STATUS_MAP: Record<OrderResultsTab, OrderStatusEnum[]> = {
  [OrderResultsTab.NEW_RESULTS]: [OrderStatusEnum.REVIEW],
  [OrderResultsTab.PENDING_RESULTS]: [OrderStatusEnum.PENDING],
  [OrderResultsTab.ORDER_CLOSED]: [
    OrderStatusEnum.COMPLETE,
    OrderStatusEnum.CLOSED,
  ],
};

export default function TeleophthStandaloneResults() {
  const { t: tPages } = useTranslation(I18NextNS.PAGES);
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);
  const { isLoadingSites, existingSites, getExistingSiteData } =
    useGetExistingSiteData();

  const [activeTab, setActiveTab] = useState<OrderResultsTab>(
    OrderResultsTab.NEW_RESULTS,
  );
  const [currentOrdersPage, setCurrentOrdersPage] = useState(1);
  const [showViewer, setShowViewer] = useState<boolean>(false);
  const [searchValues, setSearchValues] = useState<SearchPatientFormValues>();

  const {
    getFilteredOrders,
    getFilteredOrdersCore,
    ordersData,
    isGettingOrders,
  } = useGetFilterOrder();
  const { deviceDataModelId } = useGetRetinalExamDataModelConfig();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const { selectedSiteId, gradingReceivedCountResetToken } = useLocalStore();

  const {
    userSettings: { userPermission },
    setSession,
  } = useRemoteBoundStore();

  const { teleophthOrderResultsColsKeys, setTeleophthOrderResultsColsKeys } =
    useLocalStore();

  const handleCloseReportViewer = useCallback(() => {
    setShowViewer(false);
  }, []);

  const handleRefreshOrders = useCallback(() => {
    getFilteredOrders({
      status: [OrderStatusEnum.REVIEW],
      siteId: selectedSiteId,
      pageNumber: 0,
      searchValues,
    });
    setCurrentOrdersPage(1);
  }, [getFilteredOrders, selectedSiteId, searchValues]);

  const onViewReport = useCallback(
    async (orderData: DeviceDataView<OrderData>) => {
      try {
        if (!userPermission.read) {
          throw new Error(
            t('message.error.noPermissionViewReport', { ns: I18NextNS.COMMON }),
          );
        }

        if (!orderData.owner?.userId) {
          throw new Error(
            t('message.error.userNotFoundInOrder', { ns: I18NextNS.COMMON }),
          );
        }

        const examResult = await Api.remote.get_exams({
          deviceDataModelId,
          ownerFilter: {
            users: [orderData.owner?.userId],
          },
          groupElements: [
            {
              key: RetinalExamKeyFiledEnum.OrderId,
              value: orderData.data?.OrderId.value,
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            },
          ],
        });

        const exam = examResult.data.content?.[0];
        if (!exam) {
          throw new Error(
            t('message.error.orderNotFound', { ns: I18NextNS.COMMON }),
          );
        }

        const freshOrderResult = await Api.remote.get_exam_order({
          groupElements: [
            {
              key: 'OrderId',
              value: orderData.data?.OrderId.value,
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            },
          ],
        });

        const freshOrder = freshOrderResult.data.content?.[0];
        if (!freshOrder) {
          throw new Error(
            t('message.error.orderNotFound', { ns: I18NextNS.COMMON }),
          );
        }

        const session = transformExamToSession(exam);
        session.order = freshOrder.data;

        setSession(session);

        setShowViewer(true);
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);

        captureUserActionError(error, 'view_teleophth_report', {
          extras: {
            errorMessage,
          },
        });

        Api.alertBox({
          title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
          message: `${t('message.error.failedToViewReport', { ns: I18NextNS.COMMON })} ${errorMessage}`,
          locale: language,
          type: 'error',
        });
      }
    },
    [
      userPermission.read,
      t,
      deviceDataModelId,
      handleHttpRequestError,
      language,
      setSession,
    ],
  );

  const currentSelectedStatus = useMemo(
    () => TAB_TO_STATUS_MAP[activeTab],
    [activeTab],
  );

  const dateColumnTitle = useMemo(() => {
    if (activeTab === OrderResultsTab.NEW_RESULTS) {
      return t('orderFields.dateResultReceived');
    }
    if (activeTab === OrderResultsTab.PENDING_RESULTS) {
      return t('orderFields.dateExamSubmitted');
    }
    return t('orderFields.dateOrderCompleted');
  }, [activeTab, t]);

  const columns: Column<DeviceDataView<OrderData>>[] = useMemo(
    () => [
      ...(activeTab === OrderResultsTab.NEW_RESULTS
        ? [
            {
              title: t('orderFields.priorityStatus'),
              dataIndex: 'priorityStatus',
              render: (data: DeviceDataView<OrderData>) => {
                return data.data?.Priority?.value === 0 ? (
                  <span className="text-warning fw-bold">
                    {t('orderFields.priorityReview')}
                  </span>
                ) : (
                  <span className="text-gray-500 fw-bold">
                    {t('orderFields.reportAvailable')}
                  </span>
                );
              },
            },
          ]
        : []),
      {
        title: dateColumnTitle,
        dataIndex: 'reportReceivedTime',
        render: (data: DeviceDataView<OrderData>) => {
          const datetime =
            activeTab === OrderResultsTab.NEW_RESULTS
              ? data.data?.ReportDatetime?.value
              : data.maxValueProvidedOn;

          const localDatetime = moment.utc(datetime).local();

          return (
            datetime && (
              <>
                <div>{localDatetime.format('YYYY-MM-DD')}</div>
                <div>{localDatetime.format('HH:mm:ss')}</div>
              </>
            )
          );
        },
      },
      {
        title: t('patientFields.DOB'),
        dataIndex: t('patientFields.DOB'),
        render: (data: DeviceDataView<OrderData>) => data.owner?.dateOfBirth,
      },
      {
        title: t('patientFields.First Name'),
        dataIndex: t('patientFields.First Name'),
        render: (data: DeviceDataView<OrderData>) => data.owner?.firstName,
      },
      {
        title: t('patientFields.Last Name'),
        dataIndex: t('patientFields.Last Name'),
        render: (data: DeviceDataView<OrderData>) => data.owner?.lastName,
      },
      {
        title: t('patientFields.PID'),
        dataIndex: t('patientFields.PID'),
        render: (data: DeviceDataView<OrderData>) => data.owner?.patientId,
      },
      {
        title: t('patientFields.siteName'),
        dataIndex: t('patientFields.siteName'),
        render: (data: DeviceDataView<OrderData>) => data.data?.SiteName?.value,
      },
      ...(activeTab === OrderResultsTab.ORDER_CLOSED
        ? [
            {
              title: t('orderFields.orderStatus'),
              dataIndex: t('orderFields.orderStatus'),
              render: (data: DeviceDataView<OrderData>) =>
                OrderStatusBadge(data.data?.Status.value),
            },
          ]
        : []),
      ...(activeTab === OrderResultsTab.NEW_RESULTS
        ? [
            {
              title: t('orderFields.actions'),
              dataIndex: t('orderFields.actions'),
              render: (data: DeviceDataView<OrderData>) => (
                <Button
                  variant="outline-primary"
                  onClick={() => onViewReport(data)}
                  className="rounded-3 px-3 py-1"
                >
                  <FontAwesomeIcon icon={faEye} />
                </Button>
              ),
            },
          ]
        : []),
    ],
    [activeTab, t, dateColumnTitle, onViewReport],
  );

  const defaultVisibleColumns = useMemo(() => {
    const defaultColumnsConfiguration = Object.fromEntries(
      columns.map((column) => [getColumnKey(column), true]),
    );

    return teleophthOrderResultsColsKeys ?? defaultColumnsConfiguration;
  }, [columns, teleophthOrderResultsColsKeys]);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    defaultVisibleColumns,
  );

  useEffect(() => {
    setVisibleColumns(defaultVisibleColumns);
  }, [defaultVisibleColumns]);

  const handleToggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const updatedVisibleColumns = {
        ...prev,
        [key]: !prev[key],
      };

      setTeleophthOrderResultsColsKeys(updatedVisibleColumns);

      return updatedVisibleColumns;
    });
  };

  const displayedColumns = useMemo(
    () => columns.filter((column) => visibleColumns[getColumnKey(column)]),
    [columns, visibleColumns],
  );

  const getTabClassName = (tab: OrderResultsTab) => {
    const isActive = activeTab === tab;
    return isActive ? 'active border-primary border-bottom-0' : 'text-primary';
  };

  useEffect(() => {
    getExistingSiteData();
  }, [getExistingSiteData]);

  const isNewResultsTabActive = useMemo(() => {
    return activeTab === OrderResultsTab.NEW_RESULTS;
  }, [activeTab]);

  const isSearchValuesEmpty = useMemo(() => {
    return (
      searchValues === undefined ||
      Object.values(searchValues).every((value) => !value)
    );
  }, [searchValues]);

  const shouldPollNewResults = useMemo(() => {
    return isNewResultsTabActive && isSearchValuesEmpty;
  }, [isNewResultsTabActive, isSearchValuesEmpty]);

  const { resetGradingReceivedCountPolling } = useLocalStore();

  useEffect(() => {
    if (shouldPollNewResults) {
      resetGradingReceivedCountPolling();
    } else {
      getFilteredOrders({
        status: currentSelectedStatus,
        siteId: selectedSiteId,
        pageNumber: 0,
        searchValues,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isPolling } = usePollFilteredOrders({
    shouldPoll: shouldPollNewResults,
    getFilteredOrdersCore: getFilteredOrdersCore,
    currentPage: currentOrdersPage,
    setCurrentPage: setCurrentOrdersPage,
    selectedSiteId,
    searchValues,
    currentSelectedStatus,
    resetToken: gradingReceivedCountResetToken,
  });

  const handleActiveTabChange = useCallback(
    (tab: OrderResultsTab) => {
      setActiveTab(tab);

      if (tab === OrderResultsTab.NEW_RESULTS && isSearchValuesEmpty) {
        resetGradingReceivedCountPolling();
      } else {
        const statuses = TAB_TO_STATUS_MAP[tab];
        getFilteredOrders({
          status: statuses,
          siteId: selectedSiteId,
          pageNumber: 0,
          searchValues,
        });
        setCurrentOrdersPage(1);
      }
    },
    [
      getFilteredOrders,
      isSearchValuesEmpty,
      resetGradingReceivedCountPolling,
      searchValues,
      selectedSiteId,
    ],
  );

  return (
    <Stack gap={3} className="h-100">
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-center"
      >
        <h3 className="mb-0">{tPages('results.title')}</h3>
      </Stack>
      <div className="text-primary d-flex gap-2 align-items-center">
        <FontAwesomeIcon icon={faEye} />
        {tPages('results.view')}
      </div>
      <Stack direction="horizontal" gap={3} className="align-items-start">
        <div className="h-100" style={{ paddingTop: '2px' }}>
          <Form.Label>{t('sites.title')}</Form.Label>
          <SitesDropdown
            toggleClassName="py-2"
            isLoadingSites={isLoadingSites}
            existingSites={existingSites}
            handleSiteChange={(siteId) => {
              if (shouldPollNewResults) {
                resetGradingReceivedCountPolling();
              } else {
                getFilteredOrders({
                  status: currentSelectedStatus,
                  siteId: siteId,
                  pageNumber: 0,
                  searchValues,
                });
                setCurrentOrdersPage(1);
              }
            }}
          />
        </div>
        <SearchPatientInfoFormGroup
          onSearchValuesChange={(searchValues) => {
            setSearchValues(searchValues);

            if (
              isNewResultsTabActive &&
              Object.values(searchValues).every((value) => !value)
            ) {
              resetGradingReceivedCountPolling();
            } else {
              getFilteredOrders({
                status: currentSelectedStatus,
                siteId: selectedSiteId,
                pageNumber: 0,
                searchValues,
              });
              setCurrentOrdersPage(1);
            }
          }}
          onFormReset={() => {
            setSearchValues(undefined);

            if (isNewResultsTabActive) {
              resetGradingReceivedCountPolling();
            } else {
              getFilteredOrders({
                status: currentSelectedStatus,
                siteId: selectedSiteId,
                pageNumber: 0,
                searchValues: undefined,
              });
              setCurrentOrdersPage(1);
            }
          }}
        />
        <div style={{ marginTop: '30px' }}>
          <ColumnConfiguration
            columns={columns}
            visibleColumns={visibleColumns}
            onToggleColumn={handleToggleColumn}
          />
        </div>
      </Stack>
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        <Stack className="h-100">
          <Row className="align-items-center">
            <Col>
              <Nav variant="tabs" className="fw-bold">
                <Nav.Item>
                  <Nav.Link
                    className={getTabClassName(OrderResultsTab.NEW_RESULTS)}
                    onClick={() =>
                      handleActiveTabChange(OrderResultsTab.NEW_RESULTS)
                    }
                  >
                    <div className="d-flex gap-1">
                      {t('orderFields.newResults')}
                      <GradingReceivedNumberBadge />
                    </div>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    className={getTabClassName(OrderResultsTab.PENDING_RESULTS)}
                    onClick={() =>
                      handleActiveTabChange(OrderResultsTab.PENDING_RESULTS)
                    }
                  >
                    {t('orderFields.pendingResults')}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    className={getTabClassName(OrderResultsTab.ORDER_CLOSED)}
                    onClick={() =>
                      handleActiveTabChange(OrderResultsTab.ORDER_CLOSED)
                    }
                  >
                    {t('orderFields.closedOrders')}
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          </Row>

          <div className="flex-grow-1" style={{ minHeight: 0 }}>
            <CustomTable
              isLoading={isGettingOrders || isPolling}
              dataSource={ordersData?.content ?? []}
              rowKey="deviceDataId"
              pagination={{
                defaultPageSize: DEFAULT_PAGE_SIZE,
                pageSizeOptions: [DEFAULT_PAGE_SIZE],
                totalPages: ordersData?.totalPages ?? 0,
                currentPage: currentOrdersPage,
                handleSetCurrentPage: setCurrentOrdersPage,
                onChangePage: (page) => {
                  getFilteredOrders({
                    siteId: selectedSiteId,
                    pageNumber: page - 1,
                    searchValues,
                    status: currentSelectedStatus,
                  });
                },
              }}
              columns={displayedColumns}
              empty={{ buttons: [] }}
            />
          </div>
        </Stack>
        {showViewer && (
          <ReportViewer
            show={showViewer}
            handleCloseReportViewer={handleCloseReportViewer}
            handleRefreshOrders={handleRefreshOrders}
          />
        )}
      </div>
    </Stack>
  );
}
